import { useHttp } from "./useHttp";
import { useToast } from "@synota-io/synota-shared-ui";
import { useAxiosMutation, UseAxiosMutationOptions } from "./useAxiosMutation";
import { useMfaEmail } from "../hooks/useMfaEmail";

type MfaMutationVariables = {
  mfa_email?: string | null;
  mfa_code?: string | null;
  totp_code?: string | null;
};

export const useAxiosMfaMutation = <
  TData = unknown,
  TVariables extends MfaMutationVariables = MfaMutationVariables,
>(
  options: UseAxiosMutationOptions<TData, TVariables>,
  {
    disableMfa,
    modalTitle,
  }: {
    modalTitle?: string;
    disableMfa?: boolean;
  } = {},
) => {
  const { totp } = useHttp();

  const toast = useToast();
  const mutation = useAxiosMutation<TData, TVariables>({ retry: 0, ...options });

  const { sendEmail } = useMfaEmail();

  return disableMfa
    ? mutation
    : {
        ...mutation,
        mutate: (...params: Parameters<typeof mutation.mutate>) => {
          sendEmail();
          const [variables, options] = params;
          totp.onOpen({
            isLoading: false,
            email: variables?.mfa_email ?? undefined,
            title: modalTitle,
            onSubmitCode(code) {
              totp.setState((current) => ({ ...current, isLoading: true }));
              const baseVariables = (variables ?? {}) as MfaMutationVariables;
              const payload = {
                ...baseVariables,
                totp_code: code,
                mfa_code: code,
              } as TVariables;
              mutation.mutate(payload, {
                ...options,
                onSuccess: (...params) => {
                  totp.setState({ isLoading: false, onSubmitCode: () => {}, title: undefined });
                  options?.onSuccess?.(...params);
                  totp.onClose();
                },
                onError: (...params) => {
                  const [error] = params;
                  if (error.response?.status === 401) {
                    toast.error(
                      error.response.data && typeof error.response.data === "string"
                        ? error.response.data
                        : "Incorrect or invalid one time code",
                    );
                  }
                  options?.onError?.(...params);
                  totp.setState((current) => ({
                    ...current,
                    isLoading: false,
                    title: undefined,
                  }));
                },
              });
            },
          });
        },
      };
};
